import React, { useState, useRef } from 'react'

import { useDispatch } from 'react-redux'
import { hideModal } from '../../../../redux/actions'
import { getEligiblesCsvTemplate, updateEligiblesBeneficiaresFile } from 'services/beneficiariesService'

import { Button, Spinner, Typography } from '@telavita-core/react-design-kit'

import { TextInput } from 'components'
import ModalGeneric from '../ModalGeneric'
import InputValidation from 'utils/InputValidation'

const toLineNumberList = (positions) => {
  if (!Array.isArray(positions)) return []
  return positions.filter((line) => line != null && line !== '')
}

const joinLineNumbersPortuguese = (positions) => {
  const lines = toLineNumberList(positions)
  if (lines.length === 0) return ''
  if (lines.length === 1) return String(lines[0])
  const allButLast = lines.slice(0, -1).join(', ')
  const last = lines[lines.length - 1]
  return `${allButLast} e ${last}`
}

const spreadsheetValidLinesPhrase = (positions) => {
  const lines = toLineNumberList(positions)
  if (lines.length === 0) return ''
  if (lines.length === 1) return `válido na linha ${lines[0]}`
  return `válido nas linhas ${joinLineNumbersPortuguese(lines)}`
}

const buildEligiblesCsvFieldValidationMessage = (fieldName, positions) => {
  const label = fieldName || 'campo'
  const linesPhrase = spreadsheetValidLinesPhrase(positions)
  const body = linesPhrase
    ? `Insira um ${label} ${linesPhrase}`
    : `Insira um ${label} de sua planilha`
  const suffix =
    label === 'CPF' ? ' (ele deve conter 11 dígitos e ser válido).' : '.'
  return `${body}${suffix}`
}

const getEligiblesUploadApiErrorMessage = (responseData) => {
  const raw = responseData?.error?.message
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

const ModalFileBeneficiaries = ({
  planGroupCode,
  onSuccessUpdate,
}) => {

  const dispatch = useDispatch()

  const inputTitle = 'Anexar lista de beneficiários'

  const hiddenInput = useRef(null)

  const [loading, setLoading] = useState(false)
  const [beneficiaryListRemoved, setBeneficiaryListRemoved] = useState([])
  const [duplicateCpfs, setDuplicateCpfs] = useState([])
  const [success, setSuccess] = useState(null)
  const [file, setFile] = useState({
    placeholder: inputTitle,
    value: null,
    isValid: true,
    message: null,
  })

  const hasBeneficiaryRemoved = beneficiaryListRemoved?.length > 0
  const hasDuplicateCpfs = duplicateCpfs?.length > 0

  const resetHiddenFileInput = () => {
    if (hiddenInput.current) hiddenInput.current.value = null
  }

  const handleDownloadModel = () => {
    getEligiblesCsvTemplate(planGroupCode)
      .then((response) => {
        const csvContent = decodeURIComponent(escape(atob(response.file)))
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'modelo-beneficiarios.csv'
        link.click()
        window.URL.revokeObjectURL(url)
      })
  }

  const handleFileInput = async (e) => {
    const selectedFiles = e.target.files
    const fileName = selectedFiles[0]?.name ?? inputTitle
    const validation = new InputValidation(selectedFiles, 'csv', {
      maxFileSize: 6000000,
    })
    const fileIsValid = await validation.validate()

    setFile({
      placeholder: fileName,
      value: fileIsValid.encodedValue,
      isValid: fileIsValid.success,
      message: fileIsValid.message,
    })
  }

  const submitFile = async (exclusion = false) => {
    setLoading(true)
    try {
      await updateEligiblesBeneficiaresFile(planGroupCode, file.value, exclusion)
      setBeneficiaryListRemoved([])
      setDuplicateCpfs([])
      setSuccess(true)
    } catch (error) {
      const status = error.response?.status
      const responseData = error.response?.data
      const apiErrorMessage = getEligiblesUploadApiErrorMessage(responseData)

      if (status === 423) {
        const { eligibles } = responseData?.data ?? {}
        setBeneficiaryListRemoved(eligibles ?? [])
      } else if (status === 406) {
        const errorInfo = responseData?.messages?.[0]?.info ?? {}
        const validationMessage = buildEligiblesCsvFieldValidationMessage(
          errorInfo.field,
          errorInfo.positions
        )
        setFile((prev) => ({
          ...prev,
          isValid: false,
          message: validationMessage,
        }))
        setSuccess(false)
        resetHiddenFileInput()
      } else if (status === 400) {
        setFile((prev) => ({
          ...prev,
          isValid: false,
          message:
            apiErrorMessage ||
            'A lista anexada não está no padrão, verifique e tente novamente. Lembrando que os campos não podem ser preenchidos com virgulas, exceto a separação das colunas.',
        }))
        setSuccess(false)
        resetHiddenFileInput()
      } else if (status === 409) {
        const repeatedCpfs = responseData?.messages?.[0]?.info?.repeated_cpfs ?? []
        setSuccess(false)
        setDuplicateCpfs(repeatedCpfs)
        setFile((prev) => ({ ...prev, isValid: false }))
      } else if (apiErrorMessage) {
        setFile((prev) => ({
          ...prev,
          isValid: false,
          message: apiErrorMessage,
        }))
        setSuccess(false)
        resetHiddenFileInput()
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalGeneric
      hasButton={!hasBeneficiaryRemoved}
      btnDisabled={!file.isValid || file.value === null}
      btnLoading={loading}
      btnText="ENVIAR LISTA DE BENEFICIÁRIOS"
      modalTitle="Inserir lista de beneficiários"
      btnOnClick={submitFile}
      onSuccessClose={onSuccessUpdate}
      feedbackShow={success}
      feedbackType="success"
      feedbackMessage="Lista de beneficiários enviada!"
      feedbackContent={
        <div className="ModalFileBeneficiaries__FeedbackContent">
          <Typography variant="content2">
            Uma vez que os dados dos seus beneficiários constem na lista, eles já podem se cadastrar no nosso site e agendar uma consulta.
          </Typography>
        </div>
      }
    >
      <div className="ModalFileBeneficiaries__WarningTag">
        <div className="ModalFileBeneficiaries__WarningTag__title">
          <Typography variant="content2" weight="bold">
            Detalhes importantes:
          </Typography>
        </div>
        <ol className="ModalFileBeneficiaries__WarningTag__list">
          <li className="ModalFileBeneficiaries__WarningTag__list__item">
            <div className="ModalFileBeneficiaries__WarningTag__item-content">
              <div className="ModalFileBeneficiaries__WarningTag__item-text">
                <Typography variant="content2">
                  Preencha os dados do beneficiário na planilha conforme modelo (formato .csv).
                </Typography>
              </div>
              <button
                type="button"
                className="ModalFileBeneficiaries__WarningTag__link"
                onClick={handleDownloadModel}
              >
                Baixar modelo
              </button>
            </div>
          </li>
          <li className="ModalFileBeneficiaries__WarningTag__list__item">
            <div className="ModalFileBeneficiaries__WarningTag__item-content">
              <Typography variant="content2">
                Ao inserir uma planilha de atualização, tenha certeza de que os beneficiários anteriores continuem listados, ou eles terão seu acesso removido.
              </Typography>
            </div>
          </li>
        </ol>
      </div>

      <div className="ModalFileBeneficiaries--image-wrapper">
        <TextInput
          addIcon
          iconProps={{ name: 'Clip', fill: '#dedede', height: '24px', width: '24px' }}
          hasError={!file.isValid || hasBeneficiaryRemoved || hasDuplicateCpfs}
          messageError={file.message}
          placeholder={file.placeholder}
        />
        <input
          ref={hiddenInput}
          id="hiddenInput-file"
          type="file"
          accept=".csv"
          onChange={handleFileInput}
        />
      </div>
      {hasDuplicateCpfs && (
        <div className='ModalFileBeneficiaries__duplicate'>
          <Typography variant='content2' color='danger'>*Há duplicação nos CPFs abaixo. Revise-os:</Typography>
          <ul className='ModalFileBeneficiaries__duplicate__list'>
            {duplicateCpfs.map((duplicateCpf) => (
              <li key={duplicateCpf.cpf} className='ModalFileBeneficiaries__duplicate__list__item'>
                <Typography variant='content2' color='danger'>
                  {duplicateCpf.cpf}: linhas {joinLineNumbersPortuguese(duplicateCpf.positions)}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasBeneficiaryRemoved &&
        <div className="ModalFileBeneficiaries__removed">
          <h6 className="ModalFileBeneficiaries__removed__title"><strong>Atenção: {beneficiaryListRemoved.length} beneficiários</strong> foram removidos nesta nova lista:</h6>

          <ul className="ModalFileBeneficiaries__removed__list">
            {beneficiaryListRemoved.map(beneficiary => {
              const { cpf, full_name, email } = beneficiary
              return (
                <li className="ModalFileBeneficiaries__removed__list--beneficiary" key={cpf}>
                  <span>{full_name ? full_name : 'Nome não especificado'}</span>
                  <p>{`CPF ${cpf}, ${email ? email : 'e-mail não especificado'}`}</p>
                </li>
              )
            })
            }
          </ul>
          <div className="ModalFileBeneficiaries__removed__warning">
            <span>Tem certeza que deseja remover os beneficiários listados acima?</span>
            <p>Ao confirmar o envio, estes nomes se tornarão inelegíveis e terão suas próximas consultas (caso haja) automaticamente canceladas.</p>
          </div>

          <div className="ModalFileBeneficiaries__removed__buttons">
            <Button
              variant='contained'
              color='danger'
              customClassName='ModalFileBeneficiaries__removed__buttons--confirm'
              onClick={() => submitFile(true)}
            >{loading ? <Spinner customClassName='SpinnerContainer' /> : 'Sim, confirmar envio'}</Button>
            <Button
              variant='outlined'
              color='danger'
              customClassName='ModalFileBeneficiaries__removed__buttons--cancel'
              onClick={() => dispatch(hideModal())}
            >Cancelar</Button>
          </div>
        </div>
      }
    </ModalGeneric>
  )
}

export default ModalFileBeneficiaries
